// src/pages/EntityFormationPage.tsx

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserRole } from '../hooks/useUserRole';
import Navigation from '../components/Navigation';
import { loadAllEntityFormationDrafts } from '../lib/intake/entityFormationDraftApi';
import { loadAllEntityFormationSubmissions, deleteEntityFormationDraft } from '../lib/intake/entityFormationSubmitApi';

interface FormItem {
  id: string;
  type: 'draft' | 'submitted';
  businessName?: string;
  createdAt: Date | null;
  updatedAt: Date | null;
  submittedAt?: Date | null;
}

const EntityFormationPage: React.FC = () => {
  const { user, role, loading: roleLoading } = useUserRole();
  const navigate = useNavigate();
  const [forms, setForms] = useState<FormItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    if (!roleLoading && role !== 'client') {
      navigate('/client-dashboard');
    }
  }, [roleLoading, role, navigate]);


  const handleCreateNew = () => {
    navigate('/intake/entity-formation');
  };

  const loadForms = useCallback(async () => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      // Load drafts and submissions in parallel
      const [drafts, submissions] = await Promise.all([
        loadAllEntityFormationDrafts(user.uid),
        loadAllEntityFormationSubmissions(user.uid),
      ]);

      // Transform drafts
      const draftForms: FormItem[] = drafts.map((draft) => ({
        id: draft.draftId,
        type: 'draft',
        businessName: draft.data.legal_business_name || undefined,
        createdAt: draft.createdAt?.toDate() || null,
        updatedAt: draft.updatedAt?.toDate() || null,
      }));

      // Transform submissions
      const submissionForms: FormItem[] = submissions.map((submission) => ({
        id: submission.submissionId,
        type: 'submitted',
        businessName: submission.data.legal_business_name || undefined,
        createdAt: submission.createdAt?.toDate() || null,
        updatedAt: submission.updatedAt?.toDate() || null,
        submittedAt: submission.submittedAt?.toDate() || undefined,
      }));

      // Combine and sort by most recent (submittedAt or updatedAt)
      const allForms = [...draftForms, ...submissionForms].sort((a, b) => {
        const aDate = a.submittedAt || a.updatedAt || a.createdAt;
        const bDate = b.submittedAt || b.updatedAt || b.createdAt;
        if (!aDate && !bDate) return 0;
        if (!aDate) return 1;
        if (!bDate) return -1;
        return bDate.getTime() - aDate.getTime();
      });

      setForms(allForms);
    } catch (err: any) {
      console.error('Error loading forms:', err);
      setError('Failed to load forms: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadForms();
    }
  }, [user, loadForms]);

  const handleFormClick = (form: FormItem, event?: React.MouseEvent) => {
    // Don't navigate if clicking on the delete button
    if (event && (event.target as HTMLElement).closest('button')) {
      return;
    }
    if (form.type === 'draft') {
      navigate(`/intake/entity-formation?draftId=${form.id}`);
    } else {
      navigate(`/entity-formation/view/${form.id}`);
    }
  };

  const handleDelete = async (form: FormItem, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row click

    if (form.type !== 'draft') {
      return;
    }

    if (!window.confirm(`Are you sure you want to delete this draft? This action cannot be undone.`)) {
      return;
    }

    try {
      setDeletingId(form.id);
      setError('');
      setSuccess('');

      await deleteEntityFormationDraft(form.id);
      
      // Remove the form from the list
      setForms((prevForms) => prevForms.filter((f) => f.id !== form.id));
      setSuccess('Draft deleted successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      console.error('Error deleting draft:', err);
      setError('Failed to delete draft: ' + err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (date: Date | null | undefined): string => {
    if (!date) return 'N/A';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  if (roleLoading || loading) {
    return (
      <div>
        <Navigation />
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <div>Loading...</div>
        </div>
      </div>
    );
  }

  if (role !== 'client') {
    return null; // Will redirect via useEffect
  }

  return (
    <div>
      <Navigation />
      <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <div>
            <h1 style={{ margin: 0 }}>Entity Formation Forms</h1>
            <p style={{ color: '#666', marginTop: '0.5rem' }}>
              View and manage your entity formation intake forms
            </p>
          </div>
          <button
            onClick={handleCreateNew}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '500',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#0056b3';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#007bff';
            }}
          >
            + Create New Form
          </button>
        </div>

        {error && (
          <div style={{
            padding: '1rem',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            borderRadius: '8px',
            marginBottom: '1.5rem',
          }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{
            padding: '1rem',
            backgroundColor: '#d4edda',
            color: '#155724',
            borderRadius: '8px',
            marginBottom: '1.5rem',
          }}>
            {success}
          </div>
        )}

        {forms.length === 0 ? (
          <div style={{
            padding: '3rem',
            textAlign: 'center',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #dee2e6',
          }}>
            <p style={{ fontSize: '1.125rem', color: '#666', marginBottom: '1rem' }}>
              No entity formation forms found.
            </p>
            <button
              onClick={handleCreateNew}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
              }}
            >
              Create Your First Form
            </button>
          </div>
        ) : (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            border: '1px solid #dee2e6',
            overflow: 'hidden',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#495057' }}>Business Name</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#495057' }}>Status</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#495057' }}>Created</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#495057' }}>Last Updated</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#495057' }}>Submitted</th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#495057' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {forms.map((form) => (
                  <tr
                    key={form.id}
                    style={{
                      borderBottom: '1px solid #dee2e6',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'white';
                    }}
                    onClick={(e) => handleFormClick(form, e)}
                  >
                    <td style={{ padding: '1rem' }}>
                      {form.businessName || <em style={{ color: '#999' }}>Untitled</em>}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <span
                        style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '12px',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          backgroundColor: form.type === 'submitted' ? '#d4edda' : '#fff3cd',
                          color: form.type === 'submitted' ? '#155724' : '#856404',
                        }}
                      >
                        {form.type === 'submitted' ? 'Submitted' : 'Draft'}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', color: '#666', fontSize: '0.875rem' }}>
                      {formatDate(form.createdAt)}
                    </td>
                    <td style={{ padding: '1rem', color: '#666', fontSize: '0.875rem' }}>
                      {formatDate(form.updatedAt)}
                    </td>
                    <td style={{ padding: '1rem', color: '#666', fontSize: '0.875rem' }}>
                      {form.submittedAt ? formatDate(form.submittedAt) : '—'}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <span 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFormClick(form, e);
                          }}
                          style={{ 
                            color: '#007bff', 
                            fontSize: '0.875rem', 
                            fontWeight: '500',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                          }}
                        >
                          {form.type === 'draft' ? 'Edit' : 'View'}
                        </span>
                        {form.type === 'draft' && (
                          <button
                            onClick={(e) => handleDelete(form, e)}
                            disabled={deletingId === form.id}
                            style={{
                              padding: '0.25rem 0.75rem',
                              backgroundColor: deletingId === form.id ? '#6c757d' : '#dc3545',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: deletingId === form.id ? 'not-allowed' : 'pointer',
                              fontSize: '0.875rem',
                              fontWeight: '500',
                            }}
                            title="Delete draft"
                          >
                            {deletingId === form.id ? 'Deleting...' : 'Delete'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default EntityFormationPage;

